import { debounce } from "lodash-es";
import {
  onMounted,
  onUnmounted,
  readonly,
  ref,
  watch,
  type WatchSource,
} from "vue";

export interface LatestListingOptions {
  /** What the listing depends on (the price, the range); a change refetches after `debounceMs`. */
  sources: WatchSource[];
  debounceMs: number;
  onError: (error: unknown) => void;
}

/**
 * Keeps a page's listing in step with what it depends on, writing only the latest answer: loads on
 * mount, refetches (debounced) when a source changes, and drops any response that a later change
 * or leaving the page has overtaken, so a slow answer for an old price can never replace the one
 * for the current price.
 *
 * Between a source change and the refetch landing, `isLoading` is already true, so a page that
 * gates sending on it never sends against a listing for the old inputs. `listingFailed` is set
 * while the latest refresh failed: what is on screen may no longer be for sale.
 *
 * `load` returns null when there is nothing to ask for (e.g. an empty range); `apply` then gets null.
 */
export function useLatestListing<T>(
  load: () => Promise<T> | null,
  apply: (data: T | null) => void,
  { sources, debounceMs, onError }: LatestListingOptions
) {
  const isLoading = ref(false);
  const listingFailed = ref(false);
  let latest = 0;

  async function refetch(): Promise<void> {
    debouncedRefetch.cancel();
    const id = ++latest;
    const request = load();
    if (request === null) {
      apply(null);
      isLoading.value = false;
      return;
    }
    isLoading.value = true;
    try {
      const data = await request;
      if (id !== latest) return;
      listingFailed.value = false;
      apply(data);
    } catch (error: unknown) {
      if (id !== latest) return;
      listingFailed.value = true;
      onError(error);
    } finally {
      if (id === latest) isLoading.value = false;
    }
  }

  const debouncedRefetch = debounce(() => void refetch(), debounceMs);

  onMounted(() => void refetch());

  onUnmounted(() => {
    debouncedRefetch.cancel();
    latest += 1; // a response still in flight must not write once the page is gone
  });

  watch(sources, () => {
    // Retire the request in flight now, not when the debounced refetch starts, so it cannot land
    // in between and re-enable sending for the old inputs.
    latest += 1;
    isLoading.value = true;
    debouncedRefetch();
  });

  return {
    isLoading: readonly(isLoading),
    listingFailed: readonly(listingFailed),
    refetch,
  };
}
